import React, { useEffect, useRef, useState } from "react";
import "lazysizes";
import "lazysizes/plugins/optimumx/ls.optimumx";
import { css, keyframes } from "@emotion/react";
import classNames from "classnames";
import range from "lodash.range";
import { Box, BoxProps, Picture } from "../../";
import { styled, EmotionComponent } from "../../styled";
import { isIE } from "../../utils";
import { Image, ImageProps } from "../Image";
import {
  WEBP_MIME_TYPE,
  SizeMapType,
  DEFAULT_SOURCE_WIDTHS_RANGE,
  DEFAULT_LAZY_LOAD_OPTIMUM_X,
  DEFAULT_LAZY_LOAD_OPTIMUM_X_SETTING,
} from "./types";
import { getSources, generateSizesAttrValue } from "./utils";

const shimmerAnimation = keyframes`
  0% { background-position: -1440px 0 }
  100% { background-position: 1440px 0 }
`;

type GreyShimmerBoxProps = BoxProps & {
  ratio?: number;
  lazyLoad?: boolean;
  isLazyloaded?: boolean;
};

const GreyShimmerBox: EmotionComponent<GreyShimmerBoxProps> = styled(
  Box
)<GreyShimmerBoxProps>`
  position: relative;
  overflow: hidden;
  background: #fafafa;
  background-image: linear-gradient(
    to right,
    #fafafa 0%,
    #edeef1 20%,
    #f6f7f8 40%,
    #fafafa 100%
  );
  ${(props) => props.isLazyloaded && `background: transparent;`}
  background-size: 1440px 100%;
  animation: ${(props) =>
    props.lazyLoad
      ? css`
          ${shimmerAnimation} 2s linear 0s infinite normal forwards
        `
      : `none`};
  ${(props) => {
    if (props.ratio) {
      return `height: 0;
        padding-bottom: ${props.ratio}%;
      `;
    }
    return null;
  }}
`;

type LazyLoadImageProps = ImageProps & {
  useAbsolute?: boolean;
  lazyload: boolean;
  lazyloaded: boolean;
};

const LazyLoadImage: EmotionComponent<LazyLoadImageProps> = styled(
  Image
)<LazyLoadImageProps>`
  visibility: ${(props) =>
    props.lazyload ? (props.lazyloaded ? "inherit" : "hidden") : "inherit"};
  ${(props) => {
    if (props.useAbsolute) {
      return `position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;`;
    }
  }}
`;

const SUPPORTED_ORIGINAL_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp"];

export type ImageResponsiveProps = {
  /* Array of desired widths to power autogenerated srcsets. Default from 100px to 2000px */
  sourceWidths?: number[];
  /* Hints for browsers to pick appropriate width for each breakpoint  */
  sizeMap?: SizeMapType;
  /* Should lazy load image*/
  lazyLoad?: boolean;
  /* lazysizes optimumx props */
  /* When used with lazysizes, this plugin gives us more control to trade perceived quality against perceived performance
  /* on HD retina devices, compared to the standard HTML responsive image, which always give you the maximum resolution.
  /* Read more here: https://github.com/aFarkas/lazysizes/blob/HEAD/plugins/optimumx */
  lazyLoadOptimumX?: number;
  lazyLoadOptimumXEnabled?: boolean;
  /* Aspect ratio of the image in percentage */
  /* This is to be used when we already know the aspect ratio of the image, so we can set the placeholder size correctly.
  /* by using the "padding-bottom" hack to the container */
  ratio?: number;
} & ImageProps;

const DEFAULT_SOURCE_WIDTHS = range(
  DEFAULT_SOURCE_WIDTHS_RANGE.START,
  DEFAULT_SOURCE_WIDTHS_RANGE.END,
  DEFAULT_SOURCE_WIDTHS_RANGE.STEP
);

export const ImageResponsive = ({
  src,
  sourceWidths = DEFAULT_SOURCE_WIDTHS,
  sizeMap,
  lazyLoad = false,
  lazyLoadOptimumX = DEFAULT_LAZY_LOAD_OPTIMUM_X,
  lazyLoadOptimumXEnabled = DEFAULT_LAZY_LOAD_OPTIMUM_X_SETTING,
  ratio,
  className,
  ...props
}: ImageResponsiveProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isLazyloaded, setIsLazyLoaded] = useState(lazyLoad ? false : true);

  useEffect(() => {
    imgRef.current?.addEventListener("lazyloaded", () => {
      setIsLazyLoaded(true);
    });
  });

  const imageSizes = generateSizesAttrValue(sizeMap);

  const { webpSrcSet, imageSrcSet, lqipSrc, ieSrc, extension } = getSources(
    src,
    sourceWidths,
    ratio
  );

  const isOptimumXEnabled =
    lazyLoad && lazyLoadOptimumXEnabled && lazyLoadOptimumX;

  const classNameImage = classNames(lazyLoad && "lazyload", className);

  const renderImage = () => {
    return (
      <LazyLoadImage
        useAbsolute={!!ratio}
        lazyload={lazyLoad}
        lazyloaded={isLazyloaded}
        {...props}
        ref={imgRef}
        maxWidth="100%"
        sizes={isOptimumXEnabled ? undefined : imageSizes}
        data-sizes={isOptimumXEnabled ? "auto" : null}
        data-optimumx={isOptimumXEnabled ? lazyLoadOptimumX : null}
        src={lazyLoad ? (!ratio ? lqipSrc : undefined) : src}
        data-src={lazyLoad ? ieSrc : null}
        srcSet={lazyLoad && imageSrcSet ? undefined : imageSrcSet}
        data-srcset={lazyLoad && imageSrcSet ? imageSrcSet : null}
        className={classNameImage}
      />
    );
  };

  // Fallback to normal Image component
  if (!SUPPORTED_ORIGINAL_IMAGE_EXTENSIONS.includes(extension)) {
    return (
      <GreyShimmerBox ratio={ratio} lazyLoad={lazyLoad}>
        <Image src={src} className={classNameImage} {...props} />
      </GreyShimmerBox>
    );
  }

  if (webpSrcSet) {
    return (
      <GreyShimmerBox
        ratio={ratio}
        lazyLoad={lazyLoad}
        isLazyloaded={isLazyloaded}
      >
        <Picture>
          {!isIE && (
            <source
              type={WEBP_MIME_TYPE}
              sizes={isOptimumXEnabled ? undefined : imageSizes}
              data-sizes={isOptimumXEnabled ? "auto" : null}
              data-srcset={webpSrcSet}
              srcSet={lazyLoad ? undefined : webpSrcSet}
            />
          )}
          {renderImage()}
        </Picture>
      </GreyShimmerBox>
    );
  }

  return (
    <GreyShimmerBox ratio={ratio} lazyLoad={lazyLoad}>
      {renderImage()}
    </GreyShimmerBox>
  );
};

export default ImageResponsive;
